import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatestWith,
  debounceTime,
  Observable,
} from 'rxjs';
import { combineLatest, filter, take } from 'rxjs/operators';
import { SubHeaderService } from 'src/app/shared/store/subheader/subheader.service';
import {
  CurrentUserModel,
  LockoutModel,
  User,
  UserDetailModel,
} from 'src/app/shared/store/user/type';
import { UserService } from 'src/app/shared/store/user/user.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { environment } from '../../../../../../environments/environment';
import { View } from '../../../../../shared/store/view/types';
import { ViewService } from '../../../../../shared/store/view/views.service';

@UntilDestroy()
@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  standalone: false,
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  userId: string;
  userEmail: string;

  user$: Observable<UserDetailModel>;
  view$: Observable<View>;
  currentUser$: Observable<CurrentUserModel>;

  apiUrl: string = environment.comparanooapiurl;

  constructor(
    private userService: UserService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
  ) {
    this.user$ = this.userService.getById.value$;
    this.view$ = this.viewService.getViews.value$;
    this.currentUser$ = this.userService.getCurrentUser.value$;
  }

  ngOnInit(): void {
    this.userId = this.route.snapshot.params['id'];
    this.userService.getById.call(this.userId);

    this.user$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@userDetails', x.contactName);
        this.userEmail = x.aspNetUserEmail;
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
      });

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'userEdit') {
          this.router.navigate(['Configuration/Users/Edit/', this.userId]);
        }
        if (x == 'userUnlock') this.activate(true);
        if (x == 'userLock') this.activate(false);
        this.subHeaderService.setTriggerAction('');
      });

    this.currentUser$
      .pipe(
        combineLatestWith(this.view$),
        filter(
          ([currentUser, view]) =>
            !!currentUser && !!view && !view.canSeeOtherUsersData,
        ),
        take(1),
      )
      .subscribe(([currentUser, view]) => {
        if (currentUser.id !== +this.userId) {
          this.router.navigate(['Configuration/Users/']);
        }
      });
  }

  ngOnDestroy() {
    this.userService.getById.reset();
    this.breadcrumbService.set('@userDetails', ' ');
  }

  private activate(activate: boolean) {
    const lockoutModel: LockoutModel = {
      email: this.userEmail,
      activate: activate,
    };

    let resultLockout$ = this.userService.lockout.call(lockoutModel);
    resultLockout$
      .pipe(
        debounceTime(200),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.userService.getById.call(this.userId);
        this.userService.getStatusActions.call(this.userId.toString());
        this.userService.lockout.reset();
      });
  }
}
